import React from 'react';
import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useTranslation } from 'react-i18next';
import HelpButton from './HelpButton';

/**
 * THE VAT entry-mode toggle. It declares whether the number in the price field
 * beside it was typed without VAT or with VAT. It never changes that number and
 * it never picks a VAT rate, so it must never show a percentage.
 *
 * `onChange` keeps MUI's (event, value) signature so a call site can hand over
 * its existing handler untouched. `value` may be null when several rows
 * disagree (SmartReceive's "mixed" header state).
 *
 * Pair it with VatConverseLine under the field, and pass help={false} on
 * repeated rows so the icon appears once per dialog.
 */
function VatModeToggle({ value, onChange, disabled, size = 'small', help = true, sx, ...rest }) {
  const { t } = useTranslation();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0, ...sx }}>
      <ToggleButtonGroup
        value={value}
        exclusive
        onChange={onChange}
        disabled={disabled}
        size={size}
        {...rest}
      >
        {/* The value="excl"/value="incl" attributes are part of the contract:
            e2e specs locate these buttons with button[value="excl"]. */}
        <ToggleButton value="excl" sx={{ px: 1, textTransform: 'none', fontSize: '11px', lineHeight: 1.2 }}>
          {t('excl. VAT')}
        </ToggleButton>
        <ToggleButton value="incl" sx={{ px: 1, textTransform: 'none', fontSize: '11px', lineHeight: 1.2 }}>
          {t('incl. VAT')}
        </ToggleButton>
      </ToggleButtonGroup>
      {help && <HelpButton text={t('vatEntryModeHelp')} size={16} sx={{ p: 0.25 }} />}
    </Box>
  );
}

export default VatModeToggle;
